// Reconcile optimistically-added cards on a list. An Adyen card-add returns as
// soon as the tokenization is AUTHORISED, leaving the card VERIFYING until Adyen's
// recurring.token webhook lands server-side and flips it to ACTIVE (model A). While
// any card is verifying, poll `refetch` so the list updates to ACTIVE on its own.
// Stops when none remain verifying, on unmount, or after a safety budget — so a
// card that never confirms doesn't poll forever (that tail is a backend
// reconciliation follow-up, not this hook's job).

import { useEffect, useMemo } from 'react';

import type { Card, Maybe } from 'types/generatedSchemaTypes';

// Mirrors the Nets readCard verification cadence. The budget caps how long a
// single mount keeps polling.
export const VERIFYING_POLL_INTERVAL_MS = 3000;
export const VERIFYING_POLL_BUDGET_MS = 90000;

export const useVerifyingCardsPoll = (
  cards: ReadonlyArray<Maybe<Pick<Card, 'status'>>> | undefined | null,
  refetch: () => unknown
): void => {
  const hasVerifyingCard = useMemo(
    () => (cards ?? []).some((card) => card?.status === 'Verifying'),
    [cards]
  );

  useEffect(() => {
    if (!hasVerifyingCard) {
      return;
    }
    const intervalId = setInterval(() => {
      refetch();
    }, VERIFYING_POLL_INTERVAL_MS);
    const budgetId = setTimeout(() => {
      clearInterval(intervalId);
    }, VERIFYING_POLL_BUDGET_MS);
    return () => {
      clearInterval(intervalId);
      clearTimeout(budgetId);
    };
  }, [hasVerifyingCard, refetch]);
};

export default useVerifyingCardsPoll;
